import { createElement } from "../../../core/js/utilities";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import {
    componentClasses as itemClasses,
    componentName as itemName,
} from "../../../core/components/accordion/accordion-item";
import AccordionItem from "./accordion-item";
import {
    backwardListFromIndex,
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    forwardListFromIndex,
    getIndexFromParam,
    schema,
    setFocusFirstAvailableItemOnList,
    views,
} from "../../../core/components/accordion/accordion";
import {
    actions,
    createStore,
    dispatchEvent,
    reducers,
    renderState,
} from "../../../core/js/state-machine";

export const validateHTML = (element) => {
    //Must have accordion items
    return !!element.querySelectorAll(`.${itemClasses.wrapper}`).length;
};

/**
 * Creates Accordion
 * @param {HTMLElement} node
 * @param {defaultProps} props must implement **validProps**
 * @returns {{
        element:HTMLElement,
        dispose:Function,
        expand:Function,
        collapse:Function,
        expandAll:Function,
        collapseAll:Function,
    }}
 */
const Accordion = (element, props) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        schema,
        validateHTML,
    });
    if (!success) return;

    const clearScreenReaderTimer = () => {
        clearTimeout(resetAnnounceTimeout);
        clearTimeout(announceTimeout);
    };

    const removeDDSEventListeners = () => {
        element.removeEventListener(eventMap.expanded, readExpandAll, { once: true });
        element.removeEventListener(eventMap.collapsed, readCollapseAll, { once: true });
    };

    /**
     * This function will use the ariaLiveElement to announce
     * when all items are expanded/collapsed from the
     * expand-all/collapse-all buttons.
     *
     * It applies the text after announceAfter variable and
     * reset it according to resetAfter
     *
     * @param {String} value what you want to announce
     * @param {String} announceAfter time to wait before adding the message
     * @param {String} resetAfter time wait before emptying the aria-live target
     * @returns {Function} (callback to be used on addEventListener target)
     */
    const announceToScreenReader =
        (value = "", announceAfter = 500, resetAfter = 2000) =>
        () => {
            clearScreenReaderTimer();
            // Add announceAfter to guarantee that
            // it wont be called before announce
            resetAfter += announceAfter;
            // Set message
            resetAnnounceTimeout = setTimeout(() => {
                ariaLiveElement.innerHTML = value;
            }, announceAfter);
            // Reset message
            announceTimeout = setTimeout(() => {
                ariaLiveElement.innerHTML = "";
            }, resetAfter);
        };

    //Update controls when for changes on the state list
    const updateControlsState = (newStateList) => {
        if (expandAllElement) {
            // If has more than 0 false items, it's not all true
            const allTrue = newStateList.reduce((acc, item, index) => {
                acc = (accordionItems[index].isInactive || item === true) && acc;
                return acc;
            }, true);
            renderState(
                allTrue ? views.controls.inactive : views.controls.active,
                expandAllElement
            );
        }
        if (collapseAllElement) {
            // If has more than 0 true items, it's not all false
            const allFalse = newStateList.reduce((acc, item, index) => {
                acc = (accordionItems[index].isInactive || item === false) && acc;
                return acc;
            }, true);
            renderState(
                allFalse ? views.controls.inactive : views.controls.active,
                collapseAllElement
            );
        }
    };

    const handleKeyDown = (event) => {
        if (!document.body.classList.contains("user-is-tabbing")) {
            return;
        }
        const activeElement = document.activeElement;
        if (!activeElement.classList.contains(itemClasses.button)) {
            // This is the item structure div>heading>button
            return;
        }
        // The item structure follows wrapper>heading>button
        // capture the item APIs to be able use data from there.
        const currentItem = activeElement.parentNode.parentNode[itemName];
        if (!currentItem) {
            return;
        }
        switch (event.keyCode) {
            // Up
            case 38:
                {
                    event.preventDefault();
                    const prevIndex =
                        currentItem.index === 0 ? accordionItems.length - 1 : currentItem.index - 1;
                    setFocusFirstAvailableItemOnList(
                        backwardListFromIndex(prevIndex, accordionItems)
                    );
                }
                break;
            // Down
            case 40:
                {
                    event.preventDefault();
                    const nextIndex =
                        currentItem.index + 1 === accordionItems.length ? 0 : currentItem.index + 1;
                    setFocusFirstAvailableItemOnList(
                        forwardListFromIndex(nextIndex, accordionItems)
                    );
                }
                break;
            case 36: // Home
                event.preventDefault();
                // BackwardListFromIndex will decrease the index.
                // as the target is 0, we can send 1
                setFocusFirstAvailableItemOnList(forwardListFromIndex(0, accordionItems));
                break;
            case 35: // End
                event.preventDefault();
                // ForwardListFromIndex will bump the index.
                // send the item before the last
                setFocusFirstAvailableItemOnList(
                    backwardListFromIndex(accordionItems.length - 1, accordionItems)
                );
                break;
        }
    };

    const handleItemNavFocus = (event) => {
        if (!document.body.classList.contains("user-is-tabbing")) {
            return;
        }
        const targetIndex = accordionItems.reduce((acc, item, index) => {
            if (item.buttonElement === event.target) acc = index;
            return acc;
        }, -1);
        if (targetIndex !== -1) {
            let action;
            if (event.type === "focus") {
                action = actions.expandItem(targetIndex, mergedProps.independent);
            } else if (event.type === "blur") {
                action = actions.collapseItem(targetIndex);
            }
            focusStore.dispatch(action);
        }
    };

    // Check whether the user is no longer within the accordion
    const handleComponentFocus = (event) => {
        if (!element.contains(event.target)) {
            focusStore.dispatch(actions.collapseAll());
        }
    };

    // API functions

    /**
     * Expands item by index
     * @param {Number|HTMLElement} index
     * @returns Boolean
     */
    const expand = (index) => {
        const target = getIndexFromParam(index, accordionItems);
        if (target === -1) return;
        clearScreenReaderTimer();
        removeDDSEventListeners();
        expandedStore.dispatch(actions.expandItem(target, mergedProps.independent));
    };

    /**
     * Collapses item by index
     * @param {Number|HTMLElement} index
     * @returns Boolean
     */
    const collapse = (index) => {
        const target = getIndexFromParam(index, accordionItems);
        if (target === -1) return;
        clearScreenReaderTimer();
        removeDDSEventListeners();
        expandedStore.dispatch(actions.collapseItem(index, mergedProps.independent));
    };

    const expandAll = () => {
        clearScreenReaderTimer();
        removeDDSEventListeners();
        element.addEventListener(eventMap.expanded, readExpandAll, { once: true });
        const action = actions.expandAll();
        expandedStore.dispatch(action);
        dispatchEvent(eventMap[action.type], element);
    };

    const collapseAll = () => {
        clearScreenReaderTimer();
        removeDDSEventListeners();
        element.addEventListener(eventMap.collapsed, readCollapseAll, { once: true });
        const action = actions.collapseAll();
        dispatchEvent(eventMap[action.type], element);
        expandedStore.dispatch(action);
    };

    const dispose = () => {
        clearScreenReaderTimer();
        removeDDSEventListeners();
        window.removeEventListener("mousedown", handleComponentFocus);
        if (expandAllElement) expandAllElement.removeEventListener("click", expandAll);
        if (collapseAllElement) collapseAllElement.removeEventListener("click", collapseAll);
        accordionItems.map((item) => {
            item.buttonElement.removeEventListener("keydown", handleKeyDown);
            item.buttonElement.removeEventListener("blur", handleItemNavFocus);
            item.buttonElement.removeEventListener("focus", handleItemNavFocus);
            item.dispose();
            return null;
        });

        element[componentName] = null;
        expandAllElement = collapseAllElement = accordionItems = element = null;
    };

    const itemsList = Array.from(element.children).filter((ele) => {
        return ele.classList.contains(itemClasses.wrapper);
    });

    const initExpandedState = itemsList.map((item) => {
        return item.classList.contains(itemClasses.expanded);
    });
    const initFocusState = itemsList.map(() => {
        return false;
    });

    //Controls expanded/collapsed items
    const expandedStore = createStore(reducers.listReducer, initExpandedState);

    // Controls which item has the focus when using keyboard navigation
    const focusStore = createStore(reducers.listReducer, initFocusState);

    const readExpandAll = announceToScreenReader(mergedProps.srExpandAll);
    const readCollapseAll = announceToScreenReader(mergedProps.srCollapseAll);

    // Controls if an item is being expanded from expand or expandAll
    const ariaLiveElement = createElement("div", {
        class: componentClasses.screenReader,
        "aria-live": "polite",
    });
    element.appendChild(ariaLiveElement);

    // External controllers
    let expandAllElement = element.querySelector(`.${componentClasses.expandAll}`);
    let collapseAllElement = element.querySelector(`.${componentClasses.collapseAll}`);

    // SetTimeout controllers.
    let announceTimeout;
    let resetAnnounceTimeout;

    // Loop through all items and update the list with AccordionItem instances.
    let accordionItems = itemsList
        .map((child, index) => {
            const instance = AccordionItem(child, {
                index,
                expandedStore,
                focusStore,
                eventMap,
                expand,
                collapse,
                wrapper: element,
            });
            if (!instance) {
                return null;
            }
            if (!instance.isInactive) {
                instance.buttonElement.addEventListener("keydown", handleKeyDown);
                instance.buttonElement.addEventListener("focus", handleItemNavFocus);
                instance.buttonElement.addEventListener("blur", handleItemNavFocus);
            }
            return instance;
        })
        .filter((item) => {
            return !!item;
        });

    // Reset focus when user is no longer within the accordion
    window.addEventListener("mousedown", handleComponentFocus);

    if (expandAllElement) expandAllElement.addEventListener("click", expandAll);
    if (collapseAllElement) collapseAllElement.addEventListener("click", collapseAll);
    if (collapseAllElement || expandAllElement) {
        // Handlers for Screen readers
        updateControlsState(expandedStore.getState());
    }

    expandedStore.subscribe(updateControlsState);

    const api = {
        element,
        dispose,
        expand,
        collapse,
        expandAll,
        collapseAll,
    };
    element[componentName] = api;

    return api;
};
export default Accordion;
